import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation, useNavigationType } from 'react-router-dom';
import { SiteHeader, SiteFooter, FloatingCta, useTheme } from './Legal';
import { CSS, Sec } from './StrategyAutomationPage';
import { SERVICES, SERVICE_BY_SLUG } from './services';
import LeadForm from './LeadForm';

const PROCESS = [
  ['A short call', 'We go through what you need and confirm it is feasible. This step is free.', 'Free'],
  ['Scope and quote', 'You receive a written scope, a fixed price and a delivery date. Work starts after you approve it.', '50% deposit'],
  ['Build and test', 'We build to the scope and test the result.', 'Nothing due'],
  ['Review and delivery', 'You review the work and request changes.', 'Balance on delivery'],
];
const COMMON_INCLUDED = [
  ['Revisions after review', 'Changes after you review the delivered work, until it matches the agreed scope.'],
  ['Setup help and support', 'A short guide to deploy and run it, plus 14 days of support after delivery.'],
];
const COMMON_FAQ = [
  ['Will my project stay confidential?', 'Yes. We sign an NDA before you share details, and ownership of the delivered code is set out in the written agreement before work starts.'],
  ['What does it cost?', 'A fixed quote after the call, based on scope. There are no hourly surprises.'],
];

const CSS_EXTRA = `
.sv-img{display:block;width:100%;aspect-ratio:16/9;object-fit:cover;background:var(--bg2);border-bottom:1px solid var(--border)}
.sv-cover{position:relative;display:block;width:100%;padding:0;border:0;background:none;cursor:zoom-in}
.sv-cover .sv-img{transition:filter .2s}.sv-cover:hover .sv-img{filter:brightness(.92)}
.sv-count{position:absolute;right:10px;bottom:10px;background:rgba(0,0,0,.72);color:#fff;font-size:12px;padding:3px 10px;border-radius:99px}
.sv-actions{display:flex;flex-wrap:wrap;gap:10px}
.ea-root .sv-foot{display:flex;flex-direction:column;gap:12px;padding:4px 16px 16px}
.sv-b{font-size:14px;padding:8px 16px}
.lb{position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,.84);display:flex;align-items:center;justify-content:center;padding:16px}
.lb-box{background:var(--paper);color:var(--ink);border:1px solid var(--border);border-radius:10px;width:100%;max-width:1040px;max-height:calc(100vh - 32px);overflow:auto}
.lb-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 16px;border-bottom:1px solid var(--border)}
.lb-head h3{font-family:'Syne',sans-serif;font-size:16px;font-weight:700;margin:0}
.lb-x,.lb-nav{display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:50%;border:1px solid var(--border);background:var(--paper);color:var(--ink);cursor:pointer;font-size:18px;line-height:1}
.lb-x:hover,.lb-nav:hover{border-color:var(--ink)}
.lb-stage{position:relative;background:var(--bg2);aspect-ratio:16/9;touch-action:pan-y}
.lb-stage img{width:100%;height:100%;object-fit:contain;display:block}
.lb-nav{position:absolute;top:50%;transform:translateY(-50%)}.lb-prev{left:10px}.lb-next{right:10px}
.lb-count{position:absolute;left:10px;bottom:10px;background:rgba(0,0,0,.72);color:#fff;font-size:12px;padding:3px 10px;border-radius:99px}
.lb-cap{padding:10px 16px 0;color:var(--muted);font-style:italic;min-height:30px}
.lb-thumbs{display:flex;gap:8px;overflow-x:auto;padding:10px 16px}
.lb-th{flex:0 0 auto;width:96px;aspect-ratio:16/9;padding:0;border:2px solid transparent;border-radius:4px;overflow:hidden;background:var(--bg2);cursor:pointer;opacity:.65}
.lb-th img{width:100%;height:100%;object-fit:cover;display:block}
.lb-th.on{border-color:var(--accent);opacity:1}
.lb-info{padding:4px 16px 18px;border-top:1px solid var(--border);margin-top:4px}
.lb-info p{margin:12px 0}
.lb-act{display:flex;flex-wrap:wrap;align-items:center;gap:12px}
.lb-note{color:var(--muted);font-size:14px}
@media(max-width:600px){.lb{padding:0}.lb-box{max-height:100vh;border-radius:0;border:0}.lb-th{width:72px}}
.sv-tags{display:flex;flex-wrap:wrap;gap:6px}
.sv-tags span{font-family:'Syne',sans-serif;font-size:12px;color:var(--muted);background:var(--bg2);border:1px solid var(--border);border-radius:3px;padding:2px 8px}
.sv-more{display:flex;flex-wrap:wrap;gap:10px}
.ea-root .sv-card{display:flex;flex-direction:column}
.ea-root .sv-card .sv-spacer{flex:1}
@media(max-width:600px){
  .sv-actions,.sv-more{gap:7px}
  .sv-b{font-size:13px;padding:8px 11px}
  .sv-tags{gap:5px}
  .sv-tags span{font-size:11px;padding:2px 6px;overflow-wrap:anywhere}
  .ea-root .sv-foot{gap:9px;padding:4px 12px 12px}
  .lb{padding:0}
  .lb-box{width:100%;max-height:100dvh;border-radius:0;border:0}
  .lb-head{padding:10px 12px}
  .lb-head h3{font-size:15px;line-height:1.3}
  .lb-stage{aspect-ratio:4/3;max-height:54dvh}
  .lb-cap{font-size:.84rem;line-height:1.5;padding:8px 12px 0;min-height:0}
  .lb-thumbs{padding:9px 12px;gap:6px}
  .lb-info{padding:4px 12px 16px}
  .lb-info p{font-size:.9rem;line-height:1.6}
  .lb-act{gap:8px}
  .lb-note{font-size:12px}
}

`;

const getImgs = (p) => p.images || (p.image ? [{ src: p.image }] : []);

function Lightbox({ project, start, onClose, track }) {
  const imgs = getImgs(project);
  const [i, setI] = useState(start);
  const closeRef = useRef(null);
  const thumbs = useRef(null);
  const touch = useRef(null);
  const step = (d) => setI((n) => (n + d + imgs.length) % imgs.length);

  useEffect(() => {
    const before = document.activeElement;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); else if (e.key === 'ArrowRight') step(1); else if (e.key === 'ArrowLeft') step(-1); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; if (before && before.focus) before.focus(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = thumbs.current && thumbs.current.children[i];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [i]);

  const cur = imgs[i];
  const isMarket = project.link && project.link.includes('mql5.com');
  return (
    <div className="lb" role="dialog" aria-modal="true" aria-label={`${project.title} screenshots`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lb-box">
        <div className="lb-head"><h3>{project.title}</h3><button ref={closeRef} className="lb-x" aria-label="Close" onClick={onClose}>×</button></div>
        {cur && (
          <>
            <div className="lb-stage" onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => { if (touch.current === null) return; const dx = e.changedTouches[0].clientX - touch.current; touch.current = null; if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1); }}>
              <img src={cur.src} alt={cur.caption || `${project.title} screenshot ${i + 1}`} />
              {imgs.length > 1 && (<>
                <button className="lb-nav lb-prev" aria-label="Previous screenshot" onClick={() => step(-1)}>‹</button>
                <button className="lb-nav lb-next" aria-label="Next screenshot" onClick={() => step(1)}>›</button>
                <span className="lb-count">{i + 1} / {imgs.length}</span>
              </>)}
            </div>
            <div className="lb-cap">{cur.caption || ''}</div>
            {imgs.length > 1 && (
              <div className="lb-thumbs" ref={thumbs}>
                {imgs.map((im, n) => <button key={im.src} className={`lb-th${n === i ? ' on' : ''}`} aria-label={`Screenshot ${n + 1}`} onClick={() => setI(n)}><img src={im.src} alt="" loading="lazy" /></button>)}
              </div>
            )}
          </>
        )}
        <div className="lb-info">
          <p>{project.desc}</p>
          {project.highlights && project.highlights.length > 0 && <div className="sv-tags" style={{ padding: 0 }}>{project.highlights.map((h) => <span key={h}>{h}</span>)}</div>}
          {project.link && (
            <div className="lb-act" style={{ marginTop: 14 }}>
              <a className="ea-btn" href={project.link} target="_blank" rel="noopener noreferrer" onClick={() => track('Project Click', { project: project.title })}>{project.linkLabel || 'View project'} ↗</a>
              {isMarket && <span className="lb-note">Full description, version history and reviews are on MQL5 Market.</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ServicePage({ onSubmitLead }) {
  const { slug } = useParams();
  const svc = SERVICE_BY_SLUG[slug];
  const navigate = useNavigate();
  const { hash } = useLocation();
  const navType = useNavigationType();
  const [theme] = useTheme();
  const [viewer, setViewer] = useState(null);
  const openViewer = (p, i = 0) => { track('Gallery Open', { project: p.title }); setViewer({ p, i }); };

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const track = (name, props = {}) => { if (window.plausible) window.plausible(name, { props }); };
  const toQuote = (label) => () => { track('CTA Click', { button: label, service: svc && svc.name }); go('quote'); };

  useEffect(() => { if (!svc) navigate('/', { replace: true }); }, [svc, navigate]);

  useEffect(() => {
    if (!svc) return undefined;
    document.title = `${svc.name} | Qire Agency`;
    if (hash) { const t = setTimeout(() => go(hash.slice(1)), 120); return () => clearTimeout(t); }
    if (navType !== 'POP') window.scrollTo(0, 0);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, hash]);

  useEffect(() => {
    const prev = document.body.style.background;
    const font = document.createElement('link');
    font.rel = 'stylesheet'; font.href = 'https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&display=swap';
    document.head.appendChild(font);
    const script = document.createElement('script');
    script.defer = true; script.dataset.domain = 'qireagency.com'; script.src = 'https://plausible.io/js/script.js';
    document.head.appendChild(script);
    window.plausible = window.plausible || function () { (window.plausible.q = window.plausible.q || []).push(arguments); };
    return () => { document.body.style.background = prev; [font, script].forEach((n) => n.parentNode && n.parentNode.removeChild(n)); };
  }, []);
  useEffect(() => { document.body.style.background = theme === 'dark' ? '#050506' : '#fff'; }, [theme]);

  if (!svc) return null;
  const hasProjects = svc.projects.length > 0;
  const others = SERVICES.filter((s) => s.slug !== svc.slug);


  return (
    <>
      <SiteHeader current="services" cta={{ label: 'Get a Quote', onClick: toQuote('Get a Quote (Header)') }} />
      <div className="ea-root" data-theme={theme}>
        <style>{CSS}{CSS_EXTRA}</style>
        <div className="ea-page">
          <header className="ea-cover">
            <div className="ea-tag">{svc.group}</div>
            <h1 className="ea-title">{svc.title}</h1>
            <p className="ea-sub">{svc.sub}</p>
            <div className="ea-meta">
              {[['Quote', 'Fixed, after a short call'], ['Delivery', 'Date agreed in writing'], ['Payment', '50% deposit, 50% on delivery'], ['Built with', svc.stack.join(' · ')]].map(([l, v]) => <div key={l}><span className="l">{l}</span><span className="v">{v}</span></div>)}
            </div>
            <p style={{ marginTop: 28, marginBottom: 0, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button className="ea-btn" onClick={toQuote('Get a Quote (Cover)')}>Get a Quote →</button>
              {hasProjects && <button className="ea-btn ghost" onClick={() => go('projects')}>View Projects</button>}
            </p>
          </header>

          <Sec n="01" title="Overview">
            <p>{svc.intro}</p>
            <p style={{ color: 'var(--muted)' }}><strong style={{ color: 'var(--ink)' }}>Who it's for: </strong>{svc.who}</p>
            {svc.featured && (
              <div className="ea-box good">{svc.featured.text} <a href={svc.featured.to} onClick={(e) => { e.preventDefault(); navigate(svc.featured.to); }}><strong>{svc.featured.cta} →</strong></a></div>
            )}
          </Sec>

          {hasProjects && (
            <Sec n="02" title="Selected projects" id="projects">
              <div className="ea-grid">
                {svc.projects.map((p) => {
                  const imgs = getImgs(p);
                  return (
                    <div className="ea-card sv-card" key={p.title}>
                      {imgs[0] && (
                        <button type="button" className="sv-cover" aria-label={`View ${p.title} screenshots`} onClick={() => openViewer(p, 0)}>
                          <img className="sv-img" src={imgs[0].src} alt={imgs[0].caption || `${p.title} screenshot`} loading="lazy" />
                          {imgs.length > 1 && <span className="sv-count">{imgs.length} screenshots</span>}
                        </button>
                      )}
                      <h3>{p.title}</h3>
                      <p>{p.desc}</p>
                      <div className="sv-spacer" />
                      <div className="sv-foot">
                        {p.tags && <div className="sv-tags">{p.tags.map((t) => <span key={t}>{t}</span>)}</div>}
                        {(imgs.length > 0 || p.link) && (
                          <div className="sv-actions">
                            {imgs.length > 0 && <button type="button" className="ea-btn ghost sv-b" onClick={() => openViewer(p, 0)}>{imgs.length > 1 ? 'View screenshots' : 'View screenshot'}</button>}
                            {p.link && <a className="ea-btn sv-b" href={p.link} target="_blank" rel="noopener noreferrer" onClick={() => track('Project Click', { project: p.title })}>{p.linkLabel || 'View project'} ↗</a>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Sec>
          )}

          <Sec n={hasProjects ? '03' : '02'} title="What's included">
            <div className="ea-grid">{[...svc.included, ...COMMON_INCLUDED].map(([t, d]) => <div className="ea-card" key={t}><h3>{t}</h3><p>{d}</p></div>)}</div>
          </Sec>

          <Sec n={hasProjects ? '04' : '03'} title="How it works">
            <div className="ea-tbl"><table>
              <thead><tr><th>Step</th><th>What happens</th><th>Payment</th></tr></thead>
              <tbody>{PROCESS.map(([t, d, p], i) => <tr key={t}><td><strong>{i + 1}. {t}</strong></td><td>{d}</td><td><span className="ea-pill">{p}</span></td></tr>)}</tbody>
            </table></div>
          </Sec>

          <Sec n={hasProjects ? '05' : '04'} title="Frequently asked questions">
            <div className="ea-list">{[...svc.faq, ...COMMON_FAQ].map(([q, a]) => <div className="ea-ind" key={q}><h3>{q}</h3><p style={{ color: 'var(--muted)', margin: 0 }}>{a}</p></div>)}</div>
          </Sec>

          <Sec n={hasProjects ? '06' : '05'} title="Other services">
            <div className="sv-more">{others.map((s) => <button key={s.slug} className="ea-btn ghost" onClick={() => navigate(`/services/${s.slug}`)}>{s.name}</button>)}</div>
          </Sec>

          <Sec n={hasProjects ? '07' : '06'} title="Request a quote" id="quote">
            <p>Tell us what you need and we will reply within 24 hours with a fixed quote, or an honest answer if it is not a fit. Please do not paste confidential details here, as we sign an NDA first. Prefer to talk? Choose the call option in the form: you send your details first, then pick a time.</p>
            <LeadForm onSubmitLead={onSubmitLead} variant="ea" formName={`Service page: ${svc.name}`} source="Service page" service={svc.name} subject={`Custom Trading - ${svc.name}`} track={track} />
          </Sec>
        </div>
        {viewer && <Lightbox project={viewer.p} start={viewer.i} onClose={() => setViewer(null)} track={track} />}
      </div>
      <SiteFooter />
      <FloatingCta label="Get a Quote" onClick={toQuote('Get a Quote (Floating)')} hideOn={['quote', 'site-footer']} />
    </>
  );
}